import React, { useState } from "react";
import { Button } from "../ui/button";
import ImportPermissionsModal from "../PermissionsManagement/ImportPermissionsModal";
import { permissionsApi } from "../PermissionsManagement/services/permissionsApi";

const ItemDetails = ({
  selectedItem,
  config,
  formData,
  onFormChange,
  validationErrors = {},
  onShowPermissions,
  groups = [], // Add groups prop for import modal
  onPermissionsImported, // Callback for when permissions are imported
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  if (!selectedItem) return null;

  const hasMultipleColumns = config.fields.length > 6;

  // Check if this is the protected Administrators group
  const isAdministratorsGroup =
    config.itemLabel === "Group" && selectedItem.name === "Administrators";

  const handleInputChange = (fieldKey, value) => {
    // Don't trim on every keystroke - let the user type spaces
    onFormChange({
      ...formData,
      [fieldKey]: value,
    });
  };

  const handlePermissionsClick = () => {
    if (onShowPermissions && selectedItem) {
      onShowPermissions({
        id: selectedItem.id,
        name: selectedItem.name,
      });
    }
  };

  const handleImportPermissionsClick = () => {
    setShowImportModal(true);
  };

  const handleImportPermissions = async (sourceGroupName) => {
    if (!selectedItem || !sourceGroupName) return;

    setIsImporting(true);
    try {
      // Send group names to the API
      await permissionsApi.importPermissions(
        sourceGroupName,
        selectedItem.name
      );

      // Close modal
      setShowImportModal(false);

      // Notify parent component if callback is provided
      if (onPermissionsImported) {
        onPermissionsImported(selectedItem.id);
      }
    } catch (error) {
      console.error("Failed to import permissions:", error);
      alert(
        `Failed to import permissions: ${error.message || "Unknown error"}`
      );
    } finally {
      setIsImporting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.key] || "";
    const hasError = validationErrors[field.key];

    // Check if this field should be disabled for Administrators group
    const isFieldDisabled =
      isAdministratorsGroup &&
      (field.key === "name" || field.key === "description");

    // Common input classes with fixed width and height for consistent alignment
    const inputClasses = `font-normal text-xs border rounded py-1 px-2 max-w-full ${
      hasError ? "border-red-500 bg-red-50" : "border-customGrey"
    } ${
      isFieldDisabled ? "bg-lightgray text-gray-500 cursor-not-allowed" : ""
    }`;

    // Special handling for different field types
    if (field.key === "status") {
      return (
        <select
          value={value}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          className={inputClasses}
          disabled={isFieldDisabled}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      );
    }

    if (field.key === "resetPasswordRequired") {
      return (
        <select
          value={value}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          className={inputClasses}
          disabled={isFieldDisabled}
        >
          <option value="No">No</option>
          <option value="Yes">Yes</option>
        </select>
      );
    }

    if (field.key === "creationDate") {
      return (
        <input
          type="datetime-local"
          value={value ? new Date(value).toISOString().slice(0, 16) : ""}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          className={inputClasses}
          disabled={true}
        />
      );
    }

    const inputElement = (
      <input
        type={field.key === "password" ? "password" : "text"}
        value={value}
        onChange={(e) => handleInputChange(field.key, e.target.value)}
        onBlur={(e) => {
          // Trim only when user finishes typing and leaves the field
          const trimmedValue = e.target.value.trim();
          if (trimmedValue !== e.target.value) {
            handleInputChange(field.key, trimmedValue);
          }
        }}
        className={inputClasses}
        disabled={isFieldDisabled}
      />
    );

    // Wrap disabled fields with tooltip
    if (isFieldDisabled) {
      return (
        <div className="relative group">
          {inputElement}
          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
            Cannot edit Administrators group {field.key}
          </div>
        </div>
      );
    }

    return inputElement;
  };

  const renderActionButton = (button) => {
    // Handle permissions button specially
    if (button.label === "Permissions") {
      return (
        <Button
          key={button.label}
          variant="wide"
          onClick={handlePermissionsClick}
        >
          {button.label}
        </Button>
      );
    }

    // Handle import permissions button
    if (button.label === "Import Permissions") {
      return (
        <Button
          key={button.label}
          variant="wide"
          onClick={handleImportPermissionsClick}
        >
          {button.label}
        </Button>
      );
    }

    // Handle other buttons (existing behavior)
    return <Button key={button.label}>{button.label}</Button>;
  };

  return (
    <div className="bg-customLightBlue">
      <h2 className="text-2xl font-semibold text-customBlue pt-2 pb-3 px-4 text-left">
        {config.itemLabel} Details
      </h2>

      <div className="flex justify-between px-4 overflow-hidden">
        <div
          className={`min-w-0 ${
            hasMultipleColumns ? "columns-2" : ""
          } space-y-3`}
        >
          {config.fields.map((field) => (
            <div key={field.key} className="flex flex-col">
              <div className="flex items-center min-w-0">
                <label className="font-semibold text-customBlue text-xs min-w-[130px] max-w-[150px] flex-shrink-0">
                  {field.label}:
                </label>
                <div className="flex-1 overflow-hidden">
                  {renderField(field)}
                </div>
              </div>
              {validationErrors[field.key] && (
                <span className="text-red-500 text-xs mt-1 ml-[150px] break-words">
                  {validationErrors[field.key]}
                </span>
              )}
            </div>
          ))}
        </div>
        {config.actionButtons && (
          <div className="flex flex-col justify-end gap-4 flex-shrink-0 mr-10">
            {config.actionButtons.map((button) => renderActionButton(button))}
          </div>
        )}
      </div>

      {/* Import Permissions Modal */}
      <ImportPermissionsModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImport={handleImportPermissions}
        groups={groups}
        currentGroupId={selectedItem?.id}
        isImporting={isImporting}
      />
    </div>
  );
};

export default ItemDetails;
